// The innernet CELL — the signature soft-organic silhouette (a rounded, slightly
// asymmetric "cell"). This is the ONE shape innernet uses wherever a bounded blob
// is called for instead of a hard circle/rectangle: the netti body envelope, the
// profile avatar, the video play button. Sourced from the canonical Figma vector.
//
// Two forms of the same path:
//   • PATH  — in the native 1071×831 viewBox (use inside an <svg viewBox=CELL.viewBox>).
//   • BBOX  — the same path normalised to objectBoundingBox units (0..1), so a single
//             global <clipPath clipPathUnits="objectBoundingBox"> scales the cell to
//             ANY element size. Reference it with CSS `clip-path: url(#innernet-cell)`.
//
// Keep both in sync if the Figma source changes — regenerate BBOX by dividing every
// x by 1071 and every y by 831.

export const CELL = {
  /** aspect ratio of the native cell (width / height) — for slots that want it. */
  aspect: 1071 / 831,
  viewBox: "0 0 1071 831",
  /** the native path space, so a renderer can convert px ↔ path units. */
  width: 1071,
  height: 831,
  /** the cell in its native 1071×831 space. */
  path:
    "M497.334 1.16325C525.97 -1.07078 563.616 0.339061 592.556 1.8424C722.597 8.59791 864.623 37.6994 963.56 132.573C1033.39 199.542 1067.06 293.906 1070.48 391.556C1074.91 496.566 1051.64 601.094 981.706 679.447C878.328 795.279 718.684 823.838 574.817 830.23C574.003 830.277 573.189 830.325 572.373 830.363C545.368 831.517 513.973 830.964 487.114 829.81C354.565 824.096 207.321 795.212 106.747 697.848C36.2793 629.634 4.37501 538.346 0.473773 439.551C-3.70138 333.811 19.2046 230.374 89.6753 151.562C192.89 36.1332 353.651 7.30254 497.334 1.16325Z",
  /** the cell normalised to a 0..1 box — feed a clipPathUnits="objectBoundingBox". */
  bbox:
    "M0.46436 0.0014C0.4911 -0.00129 0.52625 0.00041 0.55327 0.00222C0.67469 0.01035 0.8073 0.04537 0.89968 0.15953C0.96488 0.24012 0.99632 0.35368 0.99951 0.47119C1.00365 0.59755 0.98192 0.72334 0.91663 0.81763C0.8201 0.95701 0.67104 0.99138 0.53671 0.99907C0.53595 0.99913 0.53519 0.99919 0.53443 0.99923C0.50921 1.00062 0.4799 0.99996 0.45482 0.99857C0.33106 0.99169 0.19358 0.95693 0.09967 0.83977C0.03387 0.75768 0.00408 0.64783 0.00044 0.52894C-0.00346 0.4017 0.01793 0.27723 0.08373 0.18239C0.1801 0.04348 0.33021 0.00879 0.46436 0.0014Z",
  /** the id of the app-wide objectBoundingBox clipPath (mounted once by CellClipDefs). */
  clipId: "innernet-cell",
  /**
   * The SAME silhouette as a POLAR radius law — r(θ) measured from the path's
   * area centroid and normalised so r(0) === 1 (θ = 0 is +x, the long axis).
   * This is what lets a renderer that can't use `clip-path` — the netti shader,
   * a canvas fallback — draw the EXACT brand cell instead of an approximation.
   *
   * Sampled off `path` at 64 angles and fitted with even cosine harmonics
   * (h0·h2·h4·h6·h8), then folded into a quartic in `c = cos(2θ)` so a shader
   * pays one cosine and four multiply-adds:
   *
   *   r(θ) = A + B·c + C·c² + D·c³ + E·c⁴        c = cos(2θ)
   *
   * Max error vs. the sampled path: 0.14% of the radius — visually exact.
   * Regenerate these if the Figma source changes (see the sampling note above).
   */
  polar: { A: 0.90793, B: 0.13577, C: -0.01098, D: -0.0232, E: -0.00952 },
} as const;

/**
 * The RIM — a 2px stroke lying just *inside* the cell's edge, on every face the
 * product wears (law_cell_rim). It reads as a light catch on the lip of the
 * shape: the same silhouette, only seated. One number, so an avatar at 16px and
 * the same avatar baked into an OG card at 2× can never disagree about it.
 */
export const CELL_RIM_PX = 2;

/**
 * The rim in CSS px for a mark `widthPx` wide: a flat 2px at every real avatar
 * size, tapering below ~28px so the tiny inline marks (a folder's owner at
 * 16px) wear a rim rather than a ring — 2px there would be an eighth of the
 * whole face. Same law, honest at every scale.
 */
export function cellRimPx(widthPx: number): number {
  return Math.min(CELL_RIM_PX, Math.max(1, widthPx) * 0.072);
}

/**
 * The stroke-width, in the cell's native path units, that paints that rim on a
 * mark `widthPx` wide.
 *
 * SVG strokes are centred on the path, and the cell's own clip eats everything
 * outside it — so ask for double the width and keep the half that lands inside.
 */
export function cellRimStroke(widthPx: number): number {
  return (2 * cellRimPx(widthPx) * CELL.width) / Math.max(1, widthPx);
}

/** the brand cell's radius at angle θ (radians, 0 = +x / long axis), r(0) = 1. */
export function cellRadiusAt(theta: number): number {
  const { A, B, C, D, E } = CELL.polar;
  const c = Math.cos(2 * theta);
  return A + c * (B + c * (C + c * (D + c * E)));
}

/**
 * The same law as a GLSL function — inline this into any shader that draws the
 * cell so the silhouette can never drift between the CSS clip-path and the GPU.
 * Declares `float brandCellR(float th)` returning the radius at angle th (=1 at
 * th=0, ≈0.775 at th=π/2 — the brand aspect falls out of the curve itself).
 */
export const CELL_GLSL = `
float brandCellR(float th){
  float c = cos(2.0 * th);
  return ${CELL.polar.A.toFixed(5)} + c * (${CELL.polar.B.toFixed(5)} + c * (${CELL.polar.C.toFixed(5)} + c * (${CELL.polar.D.toFixed(5)} + c * (${CELL.polar.E.toFixed(5)}))));
}
`;
